<!-- subscription.vue -->
<template>
    <!-- Main container now checks if subscription data is available before rendering -->
    <div v-if="orgData && orgData.subscription" class="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <div class="flex flex-col md:flex-row md:items-center md:justify-between">
            <!-- Current subscription information -->
            <div class="mb-6 md:mb-0">
                <h2 class="text-xl font-bold text-gray-800 mb-2">
                    Your Current Subscription
                </h2>

                <div class="flex items-start">
                    <div class="flex-shrink-0 flex items-center justify-center w-10 h-10 rounded-lg bg-primary mr-3">
                        <!-- Icon now conditionally renders based on active subscription status -->
                        <svg v-if="subscriptionIsActive" xmlns="http://www.w3.org/2000/svg" class="h-6 w-6 text-white"
                            fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <!-- You can add an alternative icon here for other statuses if needed -->
                    </div>
                    <div class="text-left">
                        <!-- Data is now dynamically bound to the 'plan' computed property -->
                        <div class="font-medium text-gray-800">{{ plan.name }} Plan</div>
                        <div class="text-sm text-gray-500">{{ plan.description }}</div>
                    </div>
                </div>

                <div class="mt-4 pl-12">
                    <div class="flex items-baseline">
                        <span class="text-2xl font-bold text-gray-800">${{ plan.price }}</span>
                        <span class="text-gray-500 ml-1">/month</span>
                    </div>
                    <div class="mt-1 text-sm text-gray-500">
                        <!-- Billing date is now dynamic -->
                        Next billing date: {{ nextBillingDate }}
                    </div>
                </div>
            </div>

            <!-- Upgrade options (No changes needed here) -->
            <div class="bg-gray-50 p-4 rounded-lg border border-gray-100 md:max-w-xs w-full">
                <div class="mb-3">
                    <div class="font-medium text-gray-800 mb-1">Ready for more?</div>
                    <div class="text-sm text-gray-600">
                        Upgrade your plan to unlock additional features and capabilities.
                    </div>
                </div>

                <button @click="upgradePlan" style="cursor: pointer"
                    class="w-full py-2 px-4 bg-primary text-white rounded-lg font-medium hover:bg-primary transition-colors">
                    Upgrade Plan
                </button>
            </div>
        </div>

        <!-- Usage summary -->
        <div class="mt-8 pt-6 border-t border-gray-200">
            <h3 class="font-medium text-gray-800 mb-4">Current Usage</h3>

            <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
                <!-- All usage data is now dynamically bound to the 'usage' computed property -->
                <div class="bg-gray-50 p-4 rounded-lg">
                    <div class="text-sm text-gray-500 mb-1">Employees</div>
                    <div class="flex items-end justify-between">
                        <div class="text-xl font-medium text-gray-800">
                            {{ usage.currentEmployees }}
                        </div>
                        <div class="text-gray-500">
                            / {{ usage.maxEmployees === -1 ? "∞" : usage.maxEmployees }}
                        </div>
                    </div>
                    <div class="mt-2 h-2 bg-slate-200 rounded-full overflow-hidden">
                        <div class="h-full bg-primary rounded-full" :style="{
                            width:
                                usage.maxEmployees === -1
                                    ? '25%'
                                    : (usage.currentEmployees / usage.maxEmployees) * 100 + '%',
                        }"></div>
                    </div>
                </div>

                <div class="bg-gray-50 p-4 rounded-lg">
                    <div class="text-sm text-gray-500 mb-1">Assessment Metrics</div>
                    <div class="flex items-end justify-between">
                        <div class="text-xl font-medium text-gray-800">
                            {{ usage.currentMetrics }}
                        </div>
                        <div class="text-gray-500">/ {{ usage.maxMetrics }}</div>
                    </div>
                    <div class="mt-2 h-2 bg-slate-200 rounded-full overflow-hidden">
                        <div class="h-full bg-primary rounded-full" :style="{
                            width: (usage.currentMetrics / usage.maxMetrics) * 100 + '%',
                        }"></div>
                    </div>
                </div>

                <div class="bg-gray-50 p-4 rounded-lg">
                    <div class="text-sm text-gray-500 mb-1">Reports Generated</div>
                    <div class="flex items-end justify-between">
                        <div class="text-xl font-medium text-gray-800">
                            {{ usage.reportsGenerated }} this month
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
    <!-- Optional: Show a loading state or a message if subscription data isn't available -->
     <SubscriptionUpgrade
      v-else-if="orgData && userData"
      :orgData="orgData"
      :userData="userData"
    />
    <div v-else>
        Loading subscription details...
    </div>
</template>

<script>
import SubscriptionUpgrade from '@/components/SubscriptionUpgrade.vue';
export default {
    name: 'SubscriptionPage',
    components: {
        SubscriptionUpgrade  
    },
    computed: {
        orgData() {
        const orgModule = this.$store.state.organization;
        return orgModule && orgModule.orgData && orgModule.orgData.data ? orgModule.orgData.data[0] : null;
        },
        userData() {
        return this.$store.state.users.data;
        }
    },
    name: "KVDRSubscriptionStatus",
    data() {
        return {
            // The 'plans' object remains as it contains static configuration data.
            // All dynamic data has been moved to computed properties.
            plans: {
                basic: {
                    id: "basic",
                    name: "Basic",
                    description: "For small teams",
                    price: 99,
                    maxEmployees: 25,
                    maxMetrics: 3,
                    features: [
                        "Up to 25 employees",
                        "3 assessment metrics",
                        "Basic reporting",
                        "Email support",
                        "Data export (CSV)",
                    ],
                },
                professional: {
                    id: "professional",
                    name: "Professional",
                    description: "For growing organizations",
                    price: 199,
                    maxEmployees: 100,
                    maxMetrics: 5,
                    features: [
                        "Up to 100 employees",
                        "All 4 KVDR metrics",
                        "Advanced visualizations",
                        "Talent pool analytics",
                        "CSV export",
                        "Priority email support",
                        "API access (read-only)",
                    ],
                    additionalFeatures: [
                        "Increase from 25 to 100 employees",
                        "Add 1 additional assessment metric",
                        "Advanced visualizations",
                        "Talent pool analytics",
                        "Priority support",
                    ],
                },
                enterprise: {
                    id: "enterprise",
                    name: "Enterprise",
                    description: "For large organizations",
                    price: 399,
                    maxEmployees: -1, // -1 can represent unlimited
                    maxMetrics: 6,
                    features: [
                        "Unlimited employees",
                        "All KVDR metrics + custom metrics",
                        "Advanced visualizations",
                        "Talent pool analytics",
                        "API integration (read/write)",
                        "Dedicated account manager",
                        "Custom reporting",
                        "SSO integration",
                    ],
                    additionalFeatures: [
                        "Unlimited employees",
                        "Custom metrics beyond the standard 4",
                        "Full API access with write capabilities",
                        "Dedicated account manager",
                        "Custom reporting",
                        "SSO integration",
                    ],
                },
            },
        };
    },
    computed: {
        orgData() {
            return this.$store.state.organization.orgData;
        },
        userData() {
            return this.$store.state.users.data;
        },
        // Computed property to get the current plan based on orgData from the store
        plan() {
            if (!this.orgData?.subscription?.planId) {
                // Return a default plan (e.g., basic) or an empty object to avoid errors
                return this.plans.basic; 
            }
            return this.plans[this.orgData.subscription.planId];
        },
        // Computed property to safely check the subscription status
        subscriptionIsActive() {
            return this.orgData?.subscription?.status === 'active';
        },
        // Computed property to format the next billing date from the store
        nextBillingDate() {
            const date = this.orgData?.subscription?.nextBillingDate;
            if (!date) {
                return "Not available";
            }
            // Formats the date string into a more readable format
            return new Date(date).toLocaleDateString("en-US", {
                year: "numeric",
                month: "long",
                day: "numeric",
            });
        },
        // Computed property for all usage statistics
        usage() {
            const orgUsage = this.orgData?.subscription?.usage;
            return {
                currentEmployees: orgUsage?.employees || 0,
                maxEmployees: this.plan.maxEmployees,
                currentMetrics: orgUsage?.metrics || 0,
                maxMetrics: this.plan.maxMetrics,
                reportsGenerated: orgUsage?.reportsGenerated || 0,
            };
        },
    },
    // The mounted() hook is no longer needed as computed properties handle the logic reactively
    methods: {
        // No changes are needed for the existing methods
        redirectToCheckout(url) {
            if (url) {
                window.location.href = url;
            }
        },
        async manageSubscripiton() {
            try {
                var tmp = {
                    userId: this.userData._id,
                    returnUrl: `https://beta.getboarded.com/organization/${this.orgData.slug}/home`,
                };
                this.$vs.loading();
                var res = await this.$store.dispatch(
                    "payments/createCheckoutSession",
                    tmp
                );
                this.redirectToCheckout(res.checkoutUrl);
                this.$vs.loading.close();
            } catch (error) {
                this.$vs.loading.close();
            }
        },
        async upgradePlan() {
            var tmp = {
                planType: "premium",
                billingCycle: "monthly",
                organizationId: this.orgData._id,
                userId: this.userData._id,
                successUrl: `http://localhost:8080/organization/${this.orgData.slug}/home`,
                cancelUrl: `http://localhost:8080/organization/${this.orgData.slug}/settings`,
            };
            try {
                this.$vs.loading();
                var res = await this.$store.dispatch(
                    "payments/createCheckoutSession",
                    tmp
                );
                this.redirectToCheckout(res.checkoutUrl);
                this.$vs.loading.close();
            } catch (error) {
                this.$vs.loading.close();
            }
        },
    },
};
</script>